/**
 * Compteurs d'une machine (CDC §8.2).
 *
 * L'écran suit la règle du CDC §16 : une valeur surprenante est **enregistrée**
 * puis signalée, jamais refusée. Concrètement, la saisie n'oppose aucun
 * obstacle ; c'est l'historique qui porte le signalement, en rouge, avec son
 * motif. Bloquer la saisie ferait perdre la donnée et renverrait le problème à
 * quelqu'un qui n'est plus devant la machine.
 *
 * Le relevé étant append-only, il n'y a ni bouton « modifier » ni bouton
 * « supprimer » : corriger, c'est ajouter un relevé qui désigne celui qu'il
 * rectifie. L'absence de ces boutons n'est pas un oubli.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { useDroitsGammes } from '../auth/useDroits';
import { ApiError, api, type Compteur, type Releve } from '../api/client';
import {
  dateApresReleves,
  premiereLigneDesignee,
  proposeCommeCompteurDesEntretiens,
  relevesDejaRectifies,
} from '../domaine/compteurs';

/** L'ancre vers laquelle la carte « Prochain entretien » renvoie (« Aller aux compteurs »). */
export const ANCRE_COMPTEURS = 'compteurs-machine';

export function Compteurs({
  assetId,
  cle = 0,
  onChange,
}: {
  assetId: string;
  /**
   * Change quand quelqu'un d'AUTRE a écrit sur les compteurs (un entretien enregistré avec « relever aussi »
   * écrit un relevé) : les compteurs se relisent. Dans l'autre sens que `onChange`, jamais le même chemin.
   */
  cle?: number;
  /**
   * Appelé après toute écriture (création, désignation, relevé, rectification, archivage) : la fiche
   * rafraîchit alors la carte « Prochain entretien », qui dépend de ce que les compteurs contiennent.
   * Un rappel remonté dans la fiche, comme partout dans l'écran : rien n'est perdu si la carte n'est
   * pas encore affichée.
   */
  onChange?: () => void;
}) {
  const droits = useDroitsGammes();
  const [compteurs, setCompteurs] = useState<Compteur[]>([]);
  const [libelles, setLibelles] = useState<string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  // Chaque compteur ouvre son historique indépendamment des autres — un
  // ensemble, pas une seule valeur qui en refermerait un en ouvrant l'autre.
  const [ouverts, setOuverts] = useState<Set<string>>(new Set());

  const charger = useCallback(async () => {
    try {
      setCompteurs(await api.counters(assetId));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Compteurs illisibles.');
    } finally {
      setChargement(false);
    }
  }, [assetId]);

  // `charger`, PAS `apresEcriture` : celui-ci préviendrait la fiche, qui rappellerait les compteurs, en boucle.
  useEffect(() => {
    void charger();
  }, [charger, cle]);

  useEffect(() => {
    // Suggestion seulement : une erreur ici ne doit pas gêner l'écran.
    api.counterLabels().then(setLibelles).catch(() => undefined);
  }, []);

  /** Recharge les compteurs, puis prévient la fiche : la carte « Prochain entretien » en dépend. */
  const apresEcriture = useCallback(async () => {
    await charger();
    onChange?.();
  }, [charger, onChange]);

  const archiver = async (compteur: Compteur) => {
    if (
      !window.confirm(
        `Archiver « ${compteur.label} » ?\n\n` +
          'Ses relevés sont conservés : le compteur quitte seulement les écrans de saisie.',
      )
    ) {
      return;
    }
    try {
      await api.archiveCounter(compteur.id);
      await apresEcriture();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Archivage impossible.');
    }
  };

  /** Désigne ce compteur pour les entretiens (le précédent, s'il y en avait un, cesse de l'être), ou le retire. */
  const designer = async (compteur: Compteur, valeur: boolean) => {
    setErreur(null);
    try {
      await api.designateCounter(compteur.id, valeur);
      await apresEcriture();
    } catch (e) {
      // Posée APRÈS le rechargement, qui remet l'erreur à zéro en cas de succès.
      setErreur(e instanceof ApiError ? e.message : 'Désignation impossible.');
    }
  };

  const autreDesigne = compteurs.find((c) => c.drivesMaintenance);

  return (
    <div className="carte" id={ANCRE_COMPTEURS}>
      <div className="entete-carte">
        <h2>Compteurs</h2>
        {droits.peutGererCompteurs && (
          <button className="secondaire discret" onClick={() => setAjoutOuvert((v) => !v)}>
            {ajoutOuvert ? 'Annuler' : 'Ajouter un compteur'}
          </button>
        )}
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}

      {ajoutOuvert && (
        <FormulaireCompteur
          assetId={assetId}
          libelles={libelles}
          designeActuel={autreDesigne?.label ?? null}
          onEnregistre={() => {
            setAjoutOuvert(false);
            void apresEcriture();
          }}
          onErreur={setErreur}
        />
      )}

      {chargement ? (
        <div className="vide">Chargement…</div>
      ) : compteurs.length === 0 ? (
        <div className="vide">
          Aucun compteur. Les heures totales de la machine sont ce qui déclenche ses entretiens :
          créez-les, cochez « Compteur des entretiens », puis relevez-les.
        </div>
      ) : (
        <>
          {droits.peutReleverCompteurs && (
            <FormulaireReleveGroupe
              compteurs={compteurs}
              onEnregistre={apresEcriture}
              onErreur={setErreur}
            />
          )}
          <div className="carte-corps">
            {compteurs.map((c) => (
              <BlocCompteur
                key={c.id}
                compteur={c}
                ouvert={ouverts.has(c.id)}
                peutGerer={droits.peutGererCompteurs}
                peutReleverCompteurs={droits.peutReleverCompteurs}
                onBasculer={() =>
                  setOuverts((v) => {
                    const suite = new Set(v);
                    if (suite.has(c.id)) {
                      suite.delete(c.id);
                    } else {
                      suite.add(c.id);
                    }
                    return suite;
                  })
                }
                onArchiver={() => void archiver(c)}
                onDesigner={(valeur) => void designer(c, valeur)}
                onRectifie={() => void apresEcriture()}
                onErreur={setErreur}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Un compteur : son dernier relevé, et l'historique dépliable. */
function BlocCompteur({
  compteur,
  ouvert,
  peutGerer,
  peutReleverCompteurs,
  onBasculer,
  onArchiver,
  onDesigner,
  onRectifie,
  onErreur,
}: {
  compteur: Compteur;
  ouvert: boolean;
  /** Paramétrer le compteur : le désigner pour les entretiens, l'archiver. */
  peutGerer: boolean;
  /** Saisir un relevé, donc aussi en rectifier un. */
  peutReleverCompteurs: boolean;
  onBasculer: () => void;
  onArchiver: () => void;
  onDesigner: (valeur: boolean) => void;
  onRectifie: () => void;
  onErreur: (m: string | null) => void;
}) {
  const [historique, setHistorique] = useState<Releve[] | null>(null);

  const chargerHistorique = useCallback(async () => {
    try {
      setHistorique(await api.readings(compteur.id));
    } catch (e) {
      onErreur(e instanceof ApiError ? e.message : 'Historique illisible.');
    }
  }, [compteur.id, onErreur]);

  // Le relevé du jour se saisit dans le formulaire groupé, plus haut sur
  // l'écran : dès que son nombre change, l'historique ouvert est périmé.
  useEffect(() => {
    setHistorique(null);
  }, [compteur.nombreReleves]);

  useEffect(() => {
    if (ouvert && historique === null) {
      void chargerHistorique();
    }
  }, [ouvert, historique, chargerHistorique]);

  const unite = compteur.unit ? ` ${compteur.unit}` : '';

  return (
    <div className="compteur">
      <div className="compteur-entete">
        <div>
          <strong>{compteur.label}</strong>
          {compteur.drivesMaintenance && (
            <span
              className="etat actif"
              title="Ce compteur commande les entretiens de la machine : c'est lui que lit la carte « Prochain entretien »"
            >
              {' '}
              compteur des entretiens{' '}
            </span>
          )}
          {!compteur.monotonic && (
            <span className="etat attente" title="Ce compteur peut revenir à zéro">
              {' '}
              remise à zéro possible{' '}
            </span>
          )}
          <div className="aide">
            {compteur.dernier ? (
              <>
                Dernier relevé : <strong>{formaterValeur(compteur.dernier.value)}{unite}</strong> le{' '}
                {jour(compteur.dernier.readAt)}
                {compteur.dernier.anomaly && <span className="etat suspendu"> à vérifier </span>}
              </>
            ) : (
              'Aucun relevé pour l’instant.'
            )}
          </div>
        </div>
        <div className="actions">
          {peutGerer && (
            <button
              className="secondaire discret"
              title={
                compteur.drivesMaintenance
                  ? 'Cette machine n’aura plus de compteur pour ses entretiens'
                  : 'Les entretiens de la machine se liront sur ce compteur (heures totales). Un seul compteur par machine : le précédent, s’il y en a un, cesse de l’être.'
              }
              onClick={() => onDesigner(!compteur.drivesMaintenance)}
            >
              {compteur.drivesMaintenance ? 'Ne plus désigner' : 'Désigner pour les entretiens'}
            </button>
          )}
          <button className="secondaire discret" onClick={onBasculer}>
            {ouvert ? 'Masquer' : `Historique (${compteur.nombreReleves})`}
          </button>
          {peutGerer && (
            <button className="secondaire discret danger-discret" onClick={onArchiver}>
              Archiver
            </button>
          )}
        </div>
      </div>

      {ouvert && (
        <Historique
          compteur={compteur}
          releves={historique}
          unite={unite}
          peutRectifier={peutReleverCompteurs}
          onRectifie={() => {
            setHistorique(null);
            onRectifie();
          }}
          onErreur={onErreur}
        />
      )}
    </div>
  );
}

function Historique({
  compteur,
  releves,
  unite,
  peutRectifier,
  onRectifie,
  onErreur,
}: {
  compteur: Compteur;
  releves: Releve[] | null;
  unite: string;
  peutRectifier: boolean;
  onRectifie: () => void;
  onErreur: (m: string | null) => void;
}) {
  // Une seule rectification ouverte à la fois : le formulaire s'affiche sous la ligne concernée.
  const [enRectification, setEnRectification] = useState<string | null>(null);

  if (releves === null) {
    return <div className="vide">Chargement de l’historique…</div>;
  }
  if (releves.length === 0) {
    return <div className="vide">Aucun relevé.</div>;
  }

  const dejaRectifies = relevesDejaRectifies(releves);

  return (
    <table className="historique-compteur">
      <thead>
        <tr>
          <th style={{ width: 110 }}>Date</th>
          <th style={{ width: 130 }}>Valeur</th>
          <th style={{ width: 90 }}>Origine</th>
          <th>Observation</th>
        </tr>
      </thead>
      <tbody>
        {releves.map((r) => (
          <HistoriqueLigne
            key={r.id}
            compteur={compteur}
            releve={r}
            unite={unite}
            rectifie={dejaRectifies.has(r.id)}
            peutRectifier={peutRectifier}
            enRectification={enRectification === r.id}
            onOuvrir={() => setEnRectification(r.id)}
            onFermer={() => setEnRectification(null)}
            onRectifie={() => {
              setEnRectification(null);
              onRectifie();
            }}
            onErreur={onErreur}
          />
        ))}
      </tbody>
    </table>
  );
}

/**
 * Une ligne de l'historique. Rectifier = ajouter un relevé qui désigne celui-ci ; l'ancien reste
 * lisible, marqué « remplacé ». Un relevé déjà remplacé ne se rectifie plus : on corrige son remplaçant.
 */
function HistoriqueLigne({
  compteur,
  releve: r,
  unite,
  rectifie,
  peutRectifier,
  enRectification,
  onOuvrir,
  onFermer,
  onRectifie,
  onErreur,
}: {
  compteur: Compteur;
  releve: Releve;
  unite: string;
  rectifie: boolean;
  peutRectifier: boolean;
  enRectification: boolean;
  onOuvrir: () => void;
  onFermer: () => void;
  onRectifie: () => void;
  onErreur: (m: string | null) => void;
}) {
  const [valeur, setValeur] = useState('');
  const [note, setNote] = useState('');
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (valeur.trim() === '') return;
    setEnCours(true);
    onErreur(null);
    try {
      await api.recordReading(compteur.id, {
        value: valeur.trim(),
        // Même jour que le relevé rectifié : c'est la valeur de CE jour-là qui était fausse.
        readAt: r.readAt.slice(0, 10),
        correctsReadingId: r.id,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setValeur('');
      setNote('');
      onRectifie();
    } catch (err) {
      onErreur(err instanceof ApiError ? err.message : 'Rectification impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <>
      <tr className={r.anomaly && !rectifie ? 'ligne-anomalie' : rectifie ? 'ligne-remplacee' : undefined}>
        <td>{jour(r.readAt)}</td>
        <td>
          <strong>{formaterValeur(r.value)}</strong>
          {unite}
        </td>
        <td>{ORIGINE[r.source]}</td>
        <td>
          {rectifie && <div className="aide">Remplacé par un relevé rectifié.</div>}
          {r.anomaly && !rectifie && <div className="motif-anomalie">{r.anomalyReason}</div>}
          {r.correctsReadingId && <div className="aide">Rectifie un relevé précédent.</div>}
          {r.interventionId && (
            <div className="aide">
              Écrit par la validation d’un <Link to={`/interventions?bon=${r.interventionId}`}>bon d’intervention</Link>.
            </div>
          )}
          {r.note && <div className="aide">{r.note}</div>}
          {peutRectifier && !rectifie && !enRectification && (
            <button
              className="secondaire discret"
              title="Ajoute un relevé qui remplace celui-ci : l'ancien reste visible, rien ne s'efface"
              onClick={onOuvrir}
            >
              Rectifier
            </button>
          )}
        </td>
      </tr>
      {enRectification && (
        <tr>
          <td colSpan={4}>
            <form className="rectification-releve" onSubmit={(e) => void soumettre(e)}>
              <span className="aide">
                Valeur exacte du {jour(r.readAt)} (au lieu de {formaterValeur(r.value)}
                {unite}) :
              </span>
              <input
                type="text"
                inputMode="decimal"
                placeholder={compteur.unit ? `Valeur en ${compteur.unit}` : 'Valeur'}
                value={valeur}
                autoFocus
                onChange={(e) => setValeur(e.target.value)}
                aria-label={`Valeur rectifiée du ${jour(r.readAt)}`}
              />
              <input
                type="text"
                placeholder="Observation (facultatif)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                aria-label="Observation de la rectification"
              />
              <button disabled={enCours || valeur.trim() === ''}>
                {enCours ? 'Enregistrement…' : 'Enregistrer la rectification'}
              </button>
              <button type="button" className="secondaire discret" onClick={onFermer}>
                Annuler
              </button>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Relevé du jour pour TOUS les compteurs de la machine à la fois : une date
 * commune, une ligne par compteur, un seul bouton. Corriger un compteur à une
 * date différente reste possible en changeant la date et en ne remplissant
 * que la ligne concernée — rien n'oblige à tout saisir d'un coup.
 */
function FormulaireReleveGroupe({
  compteurs,
  onEnregistre,
  onErreur,
}: {
  compteurs: Compteur[];
  /** Rend la main une fois les compteurs relus : l'erreur ne doit être posée qu'APRÈS. */
  onEnregistre: () => Promise<void>;
  onErreur: (m: string | null) => void;
}) {
  const [date, setDate] = useState(aujourdHui());
  const [valeurs, setValeurs] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [enCours, setEnCours] = useState(false);

  const aSaisir = compteurs.filter((c) => (valeurs[c.id] ?? '').trim() !== '');

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (aSaisir.length === 0) return;

    setEnCours(true);
    onErreur(null);
    // Envoi partiel honnête : un compteur en échec ne doit pas faire perdre
    // les autres, déjà enregistrés (même principe que le dépôt de fichiers).
    const echecs: string[] = [];
    for (const c of aSaisir) {
      try {
        await api.recordReading(c.id, {
          value: (valeurs[c.id] ?? '').trim(),
          readAt: date,
          ...((notes[c.id] ?? '').trim() ? { note: (notes[c.id] ?? '').trim() } : {}),
        });
        setValeurs((v) => {
          const suite = { ...v };
          delete suite[c.id];
          return suite;
        });
        setNotes((v) => {
          const suite = { ...v };
          delete suite[c.id];
          return suite;
        });
      } catch (err) {
        echecs.push(`${c.label} : ${err instanceof ApiError ? err.message : 'échec'}`);
      }
    }
    setEnCours(false);
    // La date repart d'aujourd'hui quand tout est enregistré (voir `dateApresReleves`).
    setDate((d) => dateApresReleves(d, aujourdHui(), echecs.length));
    // On ATTEND la fin du rechargement avant d'annoncer l'échec : `charger()` remet l'erreur à zéro quand il
    // aboutit, et effaçait donc le message « Relevé partiel » aussitôt posé (un relevé refusé ne disait rien).
    await onEnregistre();
    onErreur(echecs.length > 0 ? `Relevé partiel — ${echecs.join(' · ')}` : null);
  };

  return (
    <form className="releve-groupe" onSubmit={(e) => void soumettre(e)}>
      <div className="entete-carte releve-groupe-entete">
        <h3>Relevé du jour</h3>
        <input
          type="date"
          value={date}
          max={aujourdHui()}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Date des relevés"
        />
      </div>
      <table className="releve-groupe-table">
        <thead>
          <tr>
            <th>Compteur</th>
            <th style={{ width: 150 }}>Dernier relevé</th>
            <th style={{ width: 160 }}>Nouvelle valeur</th>
            <th>Observation</th>
          </tr>
        </thead>
        <tbody>
          {compteurs.map((c) => {
            const unite = c.unit ? ` ${c.unit}` : '';
            return (
              <tr key={c.id}>
                <td>{c.label}</td>
                <td className="aide">
                  {c.dernier ? `${formaterValeur(c.dernier.value)}${unite}` : '—'}
                </td>
                <td>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={c.unit ? `Valeur en ${c.unit}` : 'Valeur'}
                    value={valeurs[c.id] ?? ''}
                    onChange={(e) => setValeurs((v) => ({ ...v, [c.id]: e.target.value }))}
                    aria-label={`Relevé pour ${c.label}`}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder="Facultatif"
                    value={notes[c.id] ?? ''}
                    onChange={(e) => setNotes((v) => ({ ...v, [c.id]: e.target.value }))}
                    aria-label={`Observation pour ${c.label}`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button disabled={enCours || aSaisir.length === 0}>
        {enCours ? 'Enregistrement…' : 'Enregistrer les relevés'}
      </button>
    </form>
  );
}

const NOUVEAU = '__nouveau__';
let compteurLigneSeq = 0;

interface LigneNouveauCompteur {
  readonly id: number;
  choix: string;
  nouveauLibelle: string;
  unit: string;
  monotonic: boolean;
  /**
   * « Compteur des entretiens ». `null` = pas encore décidé par l'utilisateur : la case est alors
   * cochée d'après le libellé (« Heures totales »). Cocher ou décocher soi-même la fixe.
   */
  designe: boolean | null;
}

function ligneVide(libelles: string[]): LigneNouveauCompteur {
  compteurLigneSeq += 1;
  return {
    id: compteurLigneSeq,
    // Direct en saisie libre s'il n'y a encore rien à choisir : un menu à
    // une seule option (« + Nouveau libellé ») n'aiderait personne.
    choix: libelles.length > 0 ? '' : NOUVEAU,
    nouveauLibelle: '',
    unit: '',
    monotonic: true,
    designe: null,
  };
}

/**
 * Création d'un ou plusieurs compteurs à la fois : une machine porte souvent
 * plusieurs mesures d'emblée (heures totales ET heures en charge d'un
 * compresseur) — les demander une par une aurait obligé à rouvrir ce
 * formulaire autant de fois.
 */
function FormulaireCompteur({
  assetId,
  libelles,
  designeActuel,
  onEnregistre,
  onErreur,
}: {
  assetId: string;
  libelles: string[];
  /** Libellé du compteur déjà désigné pour les entretiens ; il le cessera si on en désigne un autre. */
  designeActuel: string | null;
  onEnregistre: () => void;
  onErreur: (m: string | null) => void;
}) {
  const [lignes, setLignes] = useState<LigneNouveauCompteur[]>(() => [ligneVide(libelles)]);
  const [enCours, setEnCours] = useState(false);

  const majLigne = (id: number, patch: Partial<LigneNouveauCompteur>) => {
    setLignes((v) => v.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const libelle = (l: LigneNouveauCompteur) => (l.choix === NOUVEAU ? l.nouveauLibelle : l.choix);
  const aCreer = lignes.filter((l) => libelle(l).trim() !== '');
  /** Cochée par l'utilisateur, sinon d'après le libellé : « Heures totales » est presque toujours LE compteur. */
  const estDesignee = (l: LigneNouveauCompteur) =>
    l.designe ?? proposeCommeCompteurDesEntretiens(libelle(l));
  // Un seul compteur des entretiens par machine : seule la première ligne cochée est retenue.
  const ligneDesignee = premiereLigneDesignee(aCreer, estDesignee);

  /** Cocher une ligne décoche les autres, explicitement : on ne laisse pas deux cases cochées. */
  const cocher = (id: number, valeur: boolean) => {
    setLignes((v) =>
      v.map((l) => {
        if (l.id === id) return { ...l, designe: valeur };
        return valeur ? { ...l, designe: false } : l;
      }),
    );
  };

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (aCreer.length === 0) return;

    setEnCours(true);
    onErreur(null);
    // Envoi partiel honnête : un libellé en échec (doublon, etc.) ne doit
    // pas empêcher la création des autres.
    const echecs: string[] = [];
    const reussies: number[] = [];
    for (const l of aCreer) {
      try {
        const designer = l.id === ligneDesignee;
        // Le compteur des entretiens est en heures : sans unité saisie, on dit « h » plutôt que de
        // laisser le serveur refuser la désignation sur un détail que l'écran sait compléter.
        const unite = l.unit.trim() || (designer ? 'h' : '');
        await api.createCounter(assetId, {
          label: libelle(l).trim(),
          ...(unite ? { unit: unite } : {}),
          monotonic: l.monotonic,
          ...(designer ? { drivesMaintenance: true } : {}),
        });
        reussies.push(l.id);
      } catch (err) {
        echecs.push(`${libelle(l).trim()} : ${err instanceof ApiError ? err.message : 'échec'}`);
      }
    }
    setEnCours(false);
    if (echecs.length === 0) {
      onEnregistre();
    } else {
      setLignes((v) => v.filter((l) => !reussies.includes(l.id)));
      onErreur(`Création partielle — ${echecs.join(' · ')}`);
    }
  };

  return (
    <form className="nouveau-compteur" onSubmit={(e) => void soumettre(e)}>
      <table className="lignes-nouveau-compteur">
        <thead>
          <tr>
            <th>Libellé</th>
            <th style={{ width: 140 }}>Unité</th>
            <th style={{ width: 130 }}>Ne recule jamais</th>
            <th style={{ width: 150 }} title="Les heures totales de la machine : c'est lui qui commande ses entretiens">
              Compteur des entretiens
            </th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, index) => (
            <tr key={l.id}>
              <td>
                {libelles.length > 0 && (
                  <select
                    value={l.choix}
                    onChange={(e) => majLigne(l.id, { choix: e.target.value })}
                    aria-label={`Libellé du compteur ${index + 1}`}
                  >
                    <option value="">— choisir un libellé —</option>
                    {libelles.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                    <option value={NOUVEAU}>+ Nouveau libellé…</option>
                  </select>
                )}
                {l.choix === NOUVEAU && (
                  <input
                    type="text"
                    placeholder="Libellé : « Heures compresseur »"
                    value={l.nouveauLibelle}
                    onChange={(e) => majLigne(l.id, { nouveauLibelle: e.target.value })}
                    autoFocus={libelles.length === 0}
                    aria-label={`Nouveau libellé du compteur ${index + 1}`}
                  />
                )}
              </td>
              <td>
                <input
                  type="text"
                  placeholder={estDesignee(l) && libelle(l).trim() !== '' ? 'h (par défaut)' : 'h, kWh, cycles'}
                  value={l.unit}
                  onChange={(e) => majLigne(l.id, { unit: e.target.value })}
                  aria-label={`Unité du compteur ${index + 1}`}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={l.monotonic}
                  onChange={(e) => majLigne(l.id, { monotonic: e.target.checked })}
                  title="Décochez si ce compteur peut revenir à zéro"
                  aria-label={`Ne recule jamais — compteur ${index + 1}`}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={libelle(l).trim() !== '' && l.id === ligneDesignee}
                  disabled={libelle(l).trim() === ''}
                  onChange={(e) => cocher(l.id, e.target.checked)}
                  title="Les entretiens de la machine se liront sur ce compteur (heures totales). Un seul par machine."
                  aria-label={`Compteur des entretiens — compteur ${index + 1}`}
                />
              </td>
              <td>
                {lignes.length > 1 && (
                  <button
                    type="button"
                    className="secondaire discret"
                    onClick={() => setLignes((v) => v.filter((x) => x.id !== l.id))}
                    aria-label={`Retirer la ligne ${index + 1}`}
                  >
                    ✕
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {ligneDesignee !== null && designeActuel && (
        <div className="aide">
          « {designeActuel} » ne sera plus le compteur des entretiens de cette machine : un seul compteur
          commande ses entretiens.
        </div>
      )}
      <div className="actions-nouveau-compteur">
        <button
          type="button"
          className="secondaire discret"
          onClick={() => setLignes((v) => [...v, ligneVide(libelles)])}
        >
          + Ajouter une ligne
        </button>
        <button disabled={enCours || aCreer.length === 0}>
          {enCours ? 'Création…' : aCreer.length > 1 ? `Créer ${aCreer.length} compteurs` : 'Créer'}
        </button>
      </div>
    </form>
  );
}

const ORIGINE: Record<string, string> = {
  DESK: 'Bureau',
  FIELD: 'Terrain',
  IMPORT: 'Import',
  INTERVENTION: 'Bon d’intervention',
};

/** Séparateur de milliers : un index à six chiffres est illisible sans lui. */
function formaterValeur(valeur: string): string {
  const n = Number(valeur);
  if (!Number.isFinite(n)) return valeur;
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 6 });
}

function jour(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR');
}

function aujourdHui(): string {
  // Découpe de l'ISO local, et non `toISOString()` qui bascule en UTC et
  // donnerait « demain » en soirée.
  const d = new Date();
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const jourDuMois = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${jourDuMois}`;
}
